import { useSnackbar } from 'notistack'
import { useContext, useEffect, useState } from 'react'
import { FieldErrors } from 'react-hook-form'
import { useTranslation } from 'react-i18next'

import { SupplyChainContext } from 'features/globalSetup/contexts/SupplyChainContext'
import { EditSchema } from 'features/globalSetup/pages/SupplyChainList/forms/SupplyChainForm/schemas/editSchema'
import { mapGraphQLErrors } from 'providers/tanstack-query/errors'
import { useMutation } from 'providers/tanstack-query/useMutation'
import { UPDATE_SUPPLY_CHAIN } from './api/mutation'
import { Data, Variables } from './api/types'
import { UseUpdateSupplyChain, UseUpdateSupplyChainProps } from './types'

export const useUpdateSupplyChain = ({ id }: UseUpdateSupplyChainProps): UseUpdateSupplyChain => {
  const { t } = useTranslation('features/globalSetup', {
    keyPrefix: 'supplyChain.general.actions.update',
  })
  const { enqueueSnackbar } = useSnackbar()
  const { setLoading } = useContext(SupplyChainContext)
  const [errors, setErrors] = useState<FieldErrors<EditSchema>>({})

  const { isPending, mutateAsync } = useMutation<{ input: Variables }, Data>({
    mutation: UPDATE_SUPPLY_CHAIN,
    onCompleted: async (data: Data): Promise<void> => {
      const {
        updateSupplyChain: { errors, supplyChain },
      } = data

      const { name } = supplyChain
      const tParams = {
        action: 'updated',
        supplyChain: name,
      } as const

      if (errors && errors.length > 0) {
        setErrors(mapGraphQLErrors(errors))
        enqueueSnackbar(t('error', tParams), { variant: 'error' })
      } else {
        setErrors({})
        enqueueSnackbar(t('success', tParams), { variant: 'success' })
      }
    },
    queriesToInvalidate: ['getSupplyChain'],
  })

  useEffect(() => {
    setLoading(isPending)
  }, [isPending])

  const update = ({ asi, defaultSid, name }: EditSchema): void => {
    mutateAsync({
      input: {
        asi,
        defaultSid,
        id,
        name,
      },
    })
  }

  return { errors, update }
}
